import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Sparkles, Wand2, Copy, Film, Brain } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { SkeletonCard } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useT } from "@/i18n";

const DURATIONS = ["10 secondi", "15 secondi", "30 secondi", "45 secondi", "60 secondi"];
const STYLE_KEYS = ["informativo", "storytelling", "ironico", "provocatorio", "educativo", "personale"];

const stripMd = (s) => String(s || "").replace(/[*_`#>]+/g, "").trim();

export const scriptToText = (s, t) => {
  if (!s) return "";
  if (s.testo) return stripMd(s.testo);
  const scenes = (s.scene || []).map((sc) => `${sc.tempo}: ${sc.azione}${sc.voiceover ? ` — VO: ${sc.voiceover}` : ""}`).join("\n");
  return [`${t("reels.hook").toUpperCase()}\n${s.hook}`, `${t("contentAI.scenes").toUpperCase()}\n${scenes}`,
    `${t("contentAI.caption").toUpperCase()}\n${s.caption}`, `${t("reels.cta").toUpperCase()}\n${s.cta}`,
    (s.hashtag || []).join(" ")].join("\n\n");
};

const Section = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <div className="mb-1 text-xs font-mono uppercase tracking-wide text-brand-700 dark:text-brand-400">{label}</div>
    {children}
  </div>
);

const ScriptView = ({ script, t }) => {
  if (script.testo) {
    return <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-xl bg-muted/60 p-4 text-sm font-sans" data-testid="script-result">{stripMd(script.testo)}</pre>;
  }
  return (
    <div className="max-h-80 space-y-4 overflow-y-auto rounded-xl bg-muted/60 p-4 text-sm" data-testid="script-result">
      {script.hook && <Section label={t("reels.hook")} testid="script-hook"><p className="font-medium">{stripMd(script.hook)}</p></Section>}
      {script.scene?.length > 0 && (
        <Section label={t("contentAI.scenes")} testid="script-scenes">
          <ol className="space-y-2">
            {script.scene.map((sc, i) => (
              <li key={i} className="rounded-lg border border-border bg-card p-3">
                <div className="mb-1 inline-block rounded bg-brand-500/10 px-1.5 py-0.5 font-mono text-[11px] text-brand-700 dark:text-brand-300">{sc.tempo}</div>
                <p>{stripMd(sc.azione)}</p>
                {sc.voiceover && <p className="mt-1 text-muted-foreground"><span className="font-medium text-foreground">{t("contentAI.voiceover")}:</span> {stripMd(sc.voiceover)}</p>}
              </li>
            ))}
          </ol>
        </Section>
      )}
      {script.caption && <Section label={t("contentAI.caption")} testid="script-caption"><p>{stripMd(script.caption)}</p></Section>}
      {script.cta && <Section label={t("reels.cta")} testid="script-cta"><p>{stripMd(script.cta)}</p></Section>}
      {script.hashtag?.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="script-hashtags">
          {script.hashtag.map((h, i) => <span key={i} className="rounded-full bg-card px-2 py-0.5 text-xs text-muted-foreground">{h}</span>)}
        </div>
      )}
    </div>
  );
};

export function ContentAI() {
  const { t } = useT();
  const [ideas, setIdeas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [upgrade, setUpgrade] = useState({ open: false, msg: "" });
  const [scriptFor, setScriptFor] = useState(null);
  const [duration, setDuration] = useState("15 secondi");
  const [style, setStyle] = useState("storytelling");
  const [script, setScript] = useState(null);
  const [scriptLoading, setScriptLoading] = useState(false);

  const { data: ctxData, isLoading } = useQuery({
    queryKey: ["content-context"],
    queryFn: async () => (await api.get("/content-ai/context")).data,
    retry: false,
  });
  const [usage, setUsage] = useState(null);
  const currentUsage = usage || ctxData?.usage;

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/content-ai/ideas");
      setIdeas(res.data.ideas);
      setUsage(res.data.usage);
      if (!res.data.ideas?.length) toast.error(t("contentAI.emptyIdeas"));
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 402) setUpgrade({ open: true, msg: d?.message });
      else toast.error(d?.message || d || t("errors.generic"));
    } finally { setLoading(false); }
  };

  const openScript = (idea) => {
    setScript(null); setScriptFor(idea);
  };

  const genScript = async () => {
    setScriptLoading(true);
    try {
      const res = await api.post("/content-ai/script", {
        idea_title: scriptFor.titolo || scriptFor.title || "",
        hook: scriptFor.hook || "",
        duration, style: t(`contentAI.styles.${style}`),
      });
      setScript(res.data.script);
      setUsage(res.data.usage);
    } catch (err) {
      const d = err.response?.data?.detail;
      if ([402, 403].includes(err.response?.status)) { setScriptFor(null); setUpgrade({ open: true, msg: d?.message }); }
      else toast.error(d?.message || t("errors.generic"));
    } finally { setScriptLoading(false); }
  };

  const ctx = ctxData?.context;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("contentAI.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("contentAI.subtitle")}</p>
        </div>

        {isLoading ? <SkeletonCard className="h-40" /> : (
          <div className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-gradient-to-br from-brand-500/10 via-brand-700/5 to-transparent p-6 sm:p-8">
            <div className="relative">
              <div className="flex items-center gap-2 text-brand-700 dark:text-brand-400">
                <Sparkles className="h-5 w-5" />
                <span className="text-xs font-mono uppercase tracking-widest">AI Content Strategy</span>
              </div>
              {ctx?.enough_data ? (
                <>
                  <p className="mt-3 text-lg font-medium">{t("contentAI.analyzed", { n: ctx.reels_analyzed })}</p>
                  <p className="mt-1 text-muted-foreground">{t("contentAI.topTopics", { topics: ctx.top_topics.join(", ") })}</p>
                </>
              ) : (
                <p className="mt-3 text-muted-foreground">{t("contentAI.notEnough")}</p>
              )}
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <Button onClick={generate} disabled={loading} data-testid="generate-ideas-btn"
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 text-black">
                  <Wand2 className="mr-2 h-4 w-4" /> {loading ? t("common.loading") : (ideas ? t("contentAI.regenerate") : t("contentAI.generate"))}
                </Button>
                {currentUsage && (
                  <span className="text-sm text-muted-foreground" data-testid="ai-usage">
                    {currentUsage.unlimited ? t("contentAI.fairUse") : t("contentAI.usageLeft", { n: currentUsage.remaining, limit: currentUsage.limit })}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {loading && <div className="grid gap-4">{[0,1,2].map(i => <SkeletonCard key={i} className="h-24" />)}</div>}

        {ideas && ideas.length > 0 && (
          <div>
            <h2 className="mb-4 font-display text-lg font-semibold">{t("contentAI.ideasTitle")}</h2>
            <div className="space-y-3" data-testid="ideas-list">
              {ideas.map((idea, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="rounded-2xl border border-border bg-card shadow-sm">
                  <Accordion type="single" collapsible>
                    <AccordionItem value={`i${i}`} className="border-0">
                      <AccordionTrigger className="px-5 py-4 hover:no-underline" data-testid={`idea-${i}`}>
                        <div className="flex items-start gap-3 text-left">
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500/10 text-sm font-bold text-brand-700 dark:text-brand-400">{i + 1}</div>
                          <div>
                            <div className="font-semibold">{idea.titolo || idea.title}</div>
                            <div className="mt-0.5 text-sm text-muted-foreground line-clamp-1">{idea.hook}</div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-5">
                        <div className="space-y-4 pl-11">
                          <div>
                            <div className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">{t("reels.hook")}</div>
                            <p className="text-sm">"{idea.hook}"</p>
                          </div>
                          {idea.struttura && (
                            <div>
                              <div className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">{t("contentAI.structure")}</div>
                              <ul className="space-y-1 text-sm">
                                {(Array.isArray(idea.struttura) ? idea.struttura : [idea.struttura]).map((s, j) => (
                                  <li key={j} className="flex gap-2"><span className="text-brand-500">›</span>{s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div>
                            <div className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">{t("contentAI.caption")}</div>
                            <p className="rounded-lg bg-muted/60 p-3 text-sm">{idea.caption}</p>
                          </div>
                          <div>
                            <div className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">{t("reels.cta")}</div>
                            <p className="text-sm">{idea.cta}</p>
                          </div>
                          {(idea.durata_consigliata || idea.orario_consigliato) && (
                            <div className="flex flex-wrap gap-2 text-xs">
                              {idea.durata_consigliata && <span className="rounded-full bg-brand-500/10 px-2.5 py-1 text-brand-700 dark:text-brand-300">{t("common.duration")}: {idea.durata_consigliata}</span>}
                              {idea.orario_consigliato && <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-amber-700 dark:text-amber-300">{t("contentAI.bestTime")}: {idea.orario_consigliato}</span>}
                            </div>
                          )}
                          {idea.perche_funziona && (
                            <p className="rounded-lg bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-300" data-testid={`idea-why-${i}`}>{idea.perche_funziona}</p>
                          )}
                          <Button size="sm" onClick={() => openScript(idea)} data-testid={`gen-script-btn-${i}`}
                            className="rounded-xl bg-brand-500 hover:bg-brand-600 text-black">
                            <Film className="mr-2 h-4 w-4" /> {t("reels.generateScript")}
                          </Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Script dialog */}
      <Dialog open={!!scriptFor} onOpenChange={(o) => !o && setScriptFor(null)}>
        <DialogContent className="max-w-lg" data-testid="script-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("contentAI.scriptTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm font-medium">{scriptFor?.titolo || scriptFor?.title}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">{t("contentAI.scriptDuration")}</label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="script-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>{DURATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{t("contentAI.scriptStyle")}</label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="script-style"><SelectValue /></SelectTrigger>
                  <SelectContent>{STYLE_KEYS.map(s => <SelectItem key={s} value={s}>{t(`contentAI.styles.${s}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {!script ? (
              <Button onClick={genScript} disabled={scriptLoading} data-testid="script-generate-btn"
                className="w-full rounded-xl bg-brand-500 hover:bg-brand-600 text-black">
                {scriptLoading ? (<><Brain className="mr-2 h-4 w-4 animate-pulse" /> {t("common.loading")}</>) : (<><Wand2 className="mr-2 h-4 w-4" /> {t("contentAI.scriptGenerate")}</>)}
              </Button>
            ) : (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-mono uppercase tracking-wide text-muted-foreground">{t("contentAI.scriptResult")}</span>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(scriptToText(script, t)); toast.success("Copiato"); }} data-testid="script-copy-btn">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <ScriptView script={script} t={t} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <UpgradeDialog open={upgrade.open} onOpenChange={(o) => setUpgrade({ ...upgrade, open: o })} message={upgrade.msg} />
    </DashboardLayout>
  );
}
